import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import UserLayout from './UserLayout';
import { resolveImageUrl } from '../utils/image';
import './OrderHistoryPage.css';

interface OrderDetails {
  orderId: number;
  orderDate: string;
  customerInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    district: string;
    state: string;
    pincode: string;
    country: string;
  };
  products: Array<{
    id: string;
    name: string;
    price: number;
    quantity: number;
    imageUrl: string;
  }>;
  totalAmount: number;
  status: string;
}

export default function OrderHistoryPage({ user, setUser }: { user: any, setUser: (user: any) => void }) {
  const [orders, setOrders] = useState<OrderDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const storedOrders = localStorage.getItem('userOrders');
    if (storedOrders) {
      try {
        const parsedOrders = JSON.parse(storedOrders);
        const sortedOrders = parsedOrders.sort((a: OrderDetails, b: OrderDetails) => 
          new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime()
        );
        setOrders(sortedOrders);
      } catch (error) {
        console.error('Error parsing stored orders:', error);
        setOrders([]);
      }
    }
    setLoading(false);
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const s = (status || 'pending').toLowerCase();
    switch (s) {
      case 'confirmed':
        return { label: 'CONFIRMED', className: 'status-confirmed' };
      case 'shipped':
        return { label: 'PROCESSED', className: 'status-shipped' };
      case 'delivered':
        return { label: 'COMPLETED', className: 'status-delivered' };
      case 'cancelled':
        return { label: 'CANCELLED', className: 'status-cancelled' };
      case 'pending':
      default:
        return { label: 'AWAITING WHATSAPP ADVANCE', className: 'status-pending' };
    }
  };

  if (loading) {
    return (
      <UserLayout user={user} setUser={setUser} hideSlider={true}>
        <div className="order-history-page">
          <div className="order-history-container">
            <div className="orders-loading">
              <div className="orders-spinner"></div>
              <p>Loading your orders...</p>
            </div>
          </div>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout user={user} setUser={setUser} hideSlider={true}>
      <div className="order-history-page">
        <div className="order-history-container">
          <div className="order-history-header">
            <button className="back-btn" onClick={() => navigate(-1)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
              </svg>
              <span>Back</span>
            </button>
            <div className="order-history-title-wrap">
              <h1 className="order-history-title">My Order History</h1>
              <span className="order-count-pill">{orders.length} {orders.length === 1 ? 'Order' : 'Orders'}</span>
            </div>
          </div>

          {orders.length === 0 ? (
            <div className="empty-orders-card">
              <div className="empty-orders-icon">📦</div>
              <h2>No Orders Found Yet</h2>
              <p>You haven't placed any fireworks orders yet. Start exploring our categories and festival combos!</p>
              <button className="shop-now-btn" onClick={() => navigate('/category')}>
                Explore Fireworks &rarr;
              </button>
            </div>
          ) : (
            <div className="orders-list">
              {orders.map((order) => {
                const statusInfo = getStatusBadge(order.status);
                const totalItems = order.products ? order.products.reduce((acc, p) => acc + p.quantity, 0) : 0;

                return (
                  <div key={order.orderId} className="order-card">
                    <div className="order-card-header">
                      <div className="order-info-group">
                        <div className="order-id-badge">
                          <span className="order-hash">#</span>
                          <span>{order.orderId}</span>
                        </div>
                        <div className="order-date-text">
                          Placed on {formatDate(order.orderDate)}
                        </div>
                      </div>

                      <div className={`order-status-pill ${statusInfo.className}`}>
                        <span className="status-dot"></span>
                        <span>{statusInfo.label}</span>
                      </div>
                    </div>

                    <div className="order-products-container">
                      {order.products && order.products.map((product) => {
                        const imgSrc = product.imageUrl ? resolveImageUrl(product.imageUrl) : null;
                        return (
                          <div key={product.id} className="order-product-row">
                            <div className="order-product-thumb">
                              {imgSrc ? (
                                <img src={imgSrc} alt={product.name} />
                              ) : (
                                <div className="product-thumb-placeholder">🧨</div>
                              )}
                            </div>
                            <div className="order-product-meta">
                              <span className="order-product-name">{product.name}</span>
                              <div className="order-product-details">
                                <span>Qty: <strong>{product.quantity}</strong></span>
                                <span>•</span>
                                <span>Rate: ₹{product.price.toFixed(2)}</span>
                              </div>
                            </div>
                            <div className="order-product-subtotal">
                              ₹{(product.price * product.quantity).toFixed(2)}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="order-card-footer">
                      <div className="order-footer-totals">
                        <span className="footer-total-items">{totalItems} {totalItems === 1 ? 'Item' : 'Items'}</span>
                        <div className="footer-total-price">
                          <span className="total-label">Total:</span>
                          <span className="total-val">₹{order.totalAmount.toFixed(2)}</span>
                        </div>
                      </div>

                      <div className="order-footer-actions">
                        <a
                          href={`https://wa.me/917990696364?text=Hello%20Jai%20Bhavani%20Fireworks,%20I%20am%20inquiring%20about%20Order%20%23${order.orderId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="order-wa-support-btn"
                        >
                          💬 WhatsApp Support
                        </a>
                        <button 
                          className="view-details-btn"
                          onClick={() => navigate(`/order/${order.orderId}`)}
                        >
                          View Details &rarr;
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </UserLayout>
  );
}
